export function getChartOptions(
  patientData,
  patientConcData,
  creatData,
  simConcData,
  popHi,
  popMean,
  popRange,
  getCreatinineUnit,
  trackEvent,
) {
  const baselineCreat = creatData.length > 0 ? creatData[0][1] : null;
  const maxCreat = Math.max(...creatData.map((x) => x[1]));
  return {
    chart: {
      marginTop: 150,
      height: 800,
      events: {
        load: function () {
          // Track the chart load event
          trackEvent({
            category: "Chart Load",
            action: "Chart Loaded",
            name: "Chart Loaded",
          });
        },
        // TODO: fix up the text for the plot band when the chart is resized
        render: function () {
          const chart = this;
          const xAxis = chart.xAxis[0];
          const plotBandFrom = 0;
          const plotBandTo = 60;
          const bandHeight = 30; // custom height

          if (chart.customPlotBand) chart.customPlotBand.destroy();
          if (chart.customPlotBandLabel) chart.customPlotBandLabel.destroy();
          if (chart.customGradient) chart.customGradient.destroy();

          const xStart = xAxis.toPixels(plotBandFrom);
          const xEnd = xAxis.toPixels(plotBandTo);
          const width = xEnd - xStart;
          const yPosition = chart.plotTop + chart.plotHeight - bandHeight;

          const gradient = {
            linearGradient: [0, 0, width, 0], // Horizontal gradient
            stops: [
              [0, "rgba(202,231,255,0.1)"],
              [1, "rgba(202,231,255,1)"],
            ],
          };

          // Draw the reduced-height rectangle (plot band) with gradient fill
          chart.customPlotBand = chart.renderer
            .rect(xStart, yPosition, width, bandHeight)
            .attr({
              fill: gradient,
              zIndex: 3,
            })
            .add();

          let fontSize = Math.max(10, width / 35) + "px";

          let textX = xStart + width / 2;
          let textY = yPosition + bandHeight / 2;

          let minTextWidth = width * 0.9;

          chart.customPlotBandLabel = chart.renderer
            .text("Optimal window for glucarpidase", textX, textY)
            .attr({ align: "center", zIndex: 3 })
            .css({
              color: "black",
              fontWeight: "bold",
              fontSize: fontSize,
              whiteSpace: "nowrap",
              overflow: "hidden",
              // textOverflow: "ellipsis",
              width: minTextWidth + "px",
            })
            .add();
        },
      },
    },
    exporting: {
      enabled: true,
      sourceHeight: 600,
      sourceWidth: 800,
      scale: 1,
      fallbackToExportServer: false,
    },
    loading: {
      hideDuration: 1000,
      showDuration: 1000,
    },
    title: {
      text: "",
    },
    xAxis: [
      {
        tickInterval: 6,
        title: {
          text: "Hours since the start of MTX infusion",
          style: {
            fontSize: "16px",
          },
        },
        crosshair: true,
      },
    ],
    yAxis: [
      {
        // Primary yAxis
        labels: {
          format: "{value} " + getCreatinineUnit(),
          style: {
            color: "black",
          },
        },
        title: {
          text: "Creatinine (" + getCreatinineUnit() + ")",
          style: {
            color: "black",
          },
        },
        opposite: true,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        min: 0.1,
        max: maxCreat < 1 ? 1 : maxCreat,
      },
      {
        // Secondary yAxis
        type: "logarithmic",
        allowNegativeLog: true,
        // tickInterval: 1,
        min: 0.01,
        // max: 10000,
        gridLineWidth: 1,
        minorGridLineWidth: 1,
        // tickAmount: 25,
        // floor: 0.01,
        title: {
          text: "MTX (μM)",
          style: {
            color: "black",
          },
        },
        labels: {
          format: "{value} μM",
          style: {
            color: "black",
          },
        },
      },
    ],
    tooltip: {
      hideDelay: 1000,
      shared: true,
    },
    legend: {
      layout: "horizontal",
      align: "center",
      x: 0,
      verticalAlign: "top",
      floating: true,
      shadow: true,
      borderWidth: 1,
      padding: 10,
      itemMarginTop: 4,
      itemStyle: {
        lineHeight: "14px",
      },
      title: {
        text: `<span style="font-weight:100;">*click on each legend to toggle the plot on/off</span>`,
        style: {
          fontWeight: 100,
        },
      },
    },
    series: [
      {
        name: "Creatinine > 1.5x baseline",
        type: "line",
        yAxis: 0,
        data: [], // no data, just for legend
        color: "#f78f06ff",
        dashStyle: "solid",
        showInLegend: true,
        enableMouseTracking: false,
        marker: { enabled: false },
        visible: true,
        events: {
          legendItemClick: function (e) {
            // e.preventDefault(); // prevent the default action of hiding the series
            const chart = this.chart;
            const creatSeries = chart.series.find(
              (s) => s.name === "Creatinine",
            );
            if (creatSeries) {
              // check the current state of the zones to determine whether to show the highlight or not
              const showingHighlight = creatSeries.options.zones.some(
                (zone) => zone.className === "creat-above-baseline",
              );

              // Get the default and highlighted zone configs
              const baselineCreat =
                creatSeries.options.data.length > 0
                  ? creatSeries.options.data[0][1]
                  : null;
              const zonesWithHighlight = [
                {
                  value: baselineCreat
                    ? baselineCreat * 1.49
                    : Number.POSITIVE_INFINITY,
                  color: "#930AA5",
                  dashStyle: "Solid",
                  className: "creat-normal",
                },
                {
                  color: "#f78f06ff",
                  dashStyle: "solid",
                  className: "creat-above-baseline",
                  marker: {
                    enabled: true,
                    radius: 10,
                    fillColor: "#e7495e",
                    lineWidth: 2,
                    lineColor: "#fff",
                  },
                },
              ];
              const defaultZones = [
                {
                  value: baselineCreat
                    ? baselineCreat * 1.49
                    : Number.POSITIVE_INFINITY,
                  color: "#930AA5",
                  dashStyle: "Solid",
                  className: "creat-normal",
                },
                {
                  color: "#930AA5",
                  dashStyle: "Solid",
                  className: "creat-normal",
                },
              ];

              creatSeries.update(
                {
                  zones: showingHighlight ? defaultZones : zonesWithHighlight,
                },
                false,
              );
              chart.redraw();
            }
          },
        },
      },
      {
        name: "Creatinine",
        yAxis: 0,
        zIndex: 1,
        data: creatData,
        tooltip: {
          // valueSuffix: getCreatinineUnit() + `(above baseline)`,
          valueDecimals: 2,
          pointFormatter: function () {
            console.log("chart -- ", this);
            // 'this' refers to the point
            const baseline = baselineCreat;
            const unit = getCreatinineUnit();
            const above = this.y > 1.49 * baseline;
            const suffix = above ? `(>1.5x Baseline)` : "";
            return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${this.y}</b> ${unit}${suffix}<br/>`;
          },
        },
        color: "#930AA5",
        zones: [
          {
            value: baselineCreat
              ? baselineCreat * 1.49
              : Number.POSITIVE_INFINITY,
            color: "#930AA5",
            dashStyle: "Solid",
            className: "creat-normal",
          },
          {
            color: "#f78f06ff",
            dashStyle: "solid",
            className: "creat-above-baseline",
            marker: {
              enabled: true,
              radius: 10,
              fillColor: "#e7495e",
              lineWidth: 2,
              lineColor: "#fff",
            },
          },
        ],
      },
      {
        // fit - conc values from simulation
        name: "Patient's predicted elimination curve",
        yAxis: 1,
        zIndex: 1,
        data: simConcData,
        dashStyle: "ShortDash",
        tooltip: {
          valueDecimals: 2,
        },
        color: "black",
        marker: {
          enabled: false,
        },
      },
      {
        name: "Upper limit of population 95% probability",
        type: "spline",
        yAxis: 1,
        zIndex: 1,
        data: popHi,
        color: "#e7495e",
        tooltip: {
          valueDecimals: 2,
        },
        marker: {
          enabled: false,
        },
      },
      {
        name: "Population mean",
        type: "spline",
        yAxis: 1,
        zIndex: 1,
        data: popMean,
        color: "#5CA71C",
        tooltip: {
          valueDecimals: 2,
        },
        marker: {
          enabled: false,
        },
      },
      {
        name: "Population 95% probability",
        yAxis: 1,
        data: popRange,
        type: "arearange",
        linewidth: 0,
        linkedTo: ":previous",
        fillOpacity: 0.3,
        zIndex: 0,
        marker: {
          enabled: false,
        },
        color: "#c1f7c5",
        tooltip: {
          valueDecimals: 2,
        },
      },
      {
        name: "Patient's concentration values",
        yAxis: 1,
        data: patientConcData,
        lineWidth: 0,
        marker: {
          enabled: true,
          radius: 5,
        },
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
        tooltip: {
          valueDecimals: 2,
        },
        color: "black",
      },
      {
        name: "Consensus glucarpidase guideline thresholds",
        yAxis: 1,
        data: [
          [36, 30],
          [42, 10],
          [48, 5],
          [24, patientData.loadin + patientData.mainin === 24 ? 120 : 50],
        ],
        lineWidth: 0,
        marker: {
          enabled: true,
          radius: 5,
        },
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
        tooltip: {
          valueDecimals: 2,
        },
        color: "blue",
      },
      {
        name: "EMA indication ≤1 g/m2 over 36h+",
        visible: false,
        yAxis: 1,
        data: [[48, 5]],
        lineWidth: 0,
        marker: {
          enabled: true,
          radius: 5,
        },
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
        tooltip: {
          valueDecimals: 2,
        },
        color: "rgba(245, 40, 145, 0.8)", // dark pink
      },
      {
        name: "EMA indication 1-8 g/m2 over 24h",
        visible: false,
        yAxis: 1,
        data: [
          [36, 30],
          [42, 10],
          [48, 5],
        ],
        lineWidth: 0,
        marker: {
          enabled: true,
          radius: 5,
        },
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
        tooltip: {
          valueDecimals: 2,
        },
        color: "red",
      },
      {
        name: "EMA indication 8-12 g/m2 over ≤6h",
        visible: false,
        yAxis: 1,
        data: [
          [24, 50],
          [36, 30],
          [42, 10],
          [48, 5],
        ],
        lineWidth: 0,
        marker: {
          enabled: true,
          radius: 5,
        },
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
        tooltip: {
          valueDecimals: 2,
        },
        color: "#17A2B8",
      },
      {
        name: "EMA indication 3-3.5 g/m2 over ≤6h",
        visible: false,
        yAxis: 1,
        data: [
          [24, 20],
          [48, 5],
        ],
        lineWidth: 0,
        marker: {
          enabled: true,
          radius: 5,
        },
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
        tooltip: {
          valueDecimals: 2,
        },
        color: "#BA8E23",
      },
      {
        name: "EMA indication 5 g/m2 over ≤6h",
        visible: false,
        yAxis: 1,
        data: [
          [36, 10],
          [48, 6],
        ],
        lineWidth: 0,
        marker: {
          enabled: true,
          radius: 5,
        },
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
        tooltip: {
          valueDecimals: 2,
        },
        color: "brown",
      },
    ],
    plotOptions: {
      series: {
        connectNulls: true,
        stickyTracking: false,
        className: "popup-on-click",
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              floating: true,
              align: "left",
              width: 300,
              borderWidth: 1,
              x: 0,
            },
            yAxis: [
              {
                labels: {
                  align: "right",
                  x: 0,
                  y: -6,
                },
                showLastLabel: false,
              },
              {
                labels: {
                  align: "left",
                  x: 0,
                  y: -6,
                },
                showLastLabel: false,
              },
              {
                visible: false,
              },
            ],
          },
        },
        {
          condition: {
            maxWidth: 800,
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              floating: true,
              align: "left",
              width: 300,
              borderWidth: 1,
              x: 0,
            },
            yAxis: [
              {
                labels: {
                  align: "right",
                  x: 0,
                  y: -6,
                },
                showLastLabel: false,
              },
              {
                labels: {
                  align: "left",
                  x: 0,
                  y: -6,
                },
                showLastLabel: false,
              },
              {
                visible: false,
              },
            ],
          },
        },
      ],
    },
  };
}
